import type { ClassificationResult } from '@classification-testing/domain';
import { ContentType } from '@classification-testing/domain';
import { InlineAudioPlayer } from '@core/components';
import { useSystemConfig } from '@core/hooks/useSystemConfig';
import { getContentDisplayText, formatTimestamp } from '@core/utils';
import { Badge, BadgeSize, IconButton, Eye, IconButtonSize, IconSize, StatusCircleBlock } from '@soteria/design-system-ve';
import { useState } from 'react';

import * as styles from './ClassificationResultDialogV2.css';
import { LabelsSectionV2 } from './ClassificationResultDialogV2Labels';

/**
 * V2 Wide Layout Content for Classification Result Dialog
 *
 * This component renders a horizontal four-column layout for displaying
 * classification results, as an alternative to the vertical V1 layout.
 */

interface ClassificationResultDialogV2ContentProps {
  result: ClassificationResult;
  isContentBlurred: boolean;
  isSafe: boolean;
  onToggleBlur: () => void;
}

// Helper to get content type display name
function getContentTypeDisplay(contentType: ContentType): string {
  switch (contentType) {
    case ContentType.AUDIO:
      return 'audio';
    case ContentType.IMAGE:
      return 'image';
    case ContentType.TEXT:
      return 'text';
    case ContentType.USERNAME:
      return 'username';
    case ContentType.MIXED:
      return 'mixed';
    default:
      return 'unknown';
  }
}

// Helper to get classification result display
function getClassificationDisplay(isSafe: boolean): string {
  return isSafe ? 'allowed' : 'rejected';
}

// Helper to format confidence score
function formatConfidence(score: number | undefined, contentType: ContentType): string {
  if (score === undefined) return 'N/A';
  // Image confidence is already percentage, text is decimal
  const percentage = contentType === ContentType.IMAGE ? score : score * 100;

  return `${percentage.toFixed(2)}%`;
}

// Helper to format processing time (using duration as proxy if available)
function formatProcessingTime(duration: number | undefined): string {
  if (duration === undefined) return 'N/A';

  return `${duration}ms`;
}

// Metadata Row Component
function MetadataRow({ label, value }: { label: string; value: string | React.ReactNode }) {
  return (
    <div className={styles.metadataRow}>
      <span className={styles.metadataRowLabel}>{label}:</span>
      <span className={styles.metadataRowValue}>{value}</span>
    </div>
  );
}

// Content Information Column
function ContentInformationColumn({ result }: { result: ClassificationResult }) {
  return (
    <div className={styles.metadataColumn}>
      <h4 className={styles.metadataColumnTitle}>Content Information</h4>
      <MetadataRow label="Subject ID" value={result.id || 'Unknown'} />
      <MetadataRow label="Timestamp (UTC)" value={formatTimestamp(result.timestamp)} />
      <MetadataRow label="Event Type" value="Classification" />
      <MetadataRow label="Content Type" value={getContentTypeDisplay(result.contentType)} />
      <MetadataRow label="Topic Name" value="Default" />
      {result.contentUrl && (
        <div className={styles.metadataRow}>
          <span className={styles.metadataRowLabel}>Content URL:</span>
          <a className={styles.metadataRowValueLink} href={result.contentUrl} rel="noopener noreferrer" target="_blank" title={result.contentUrl}>
            {result.contentUrl.length > 60 ? `${result.contentUrl.substring(0, 60)}...` : result.contentUrl}
          </a>
        </div>
      )}
    </div>
  );
}

// Classification Results Column
function ClassificationResultsColumn({ result, isSafe }: { result: ClassificationResult; isSafe: boolean }) {
  return (
    <div className={styles.metadataColumn}>
      <h4 className={styles.metadataColumnTitle}>Classification Results</h4>
      <MetadataRow label="Confidence" value={formatConfidence(result.confidenceScore, result.contentType)} />
      <MetadataRow label="Classification" value={getClassificationDisplay(isSafe)} />
      <MetadataRow label="Processing Time" value={formatProcessingTime(result.duration)} />
    </div>
  );
}

// Policy Information Column
function PolicyInformationColumn({ result }: { result: ClassificationResult }) {
  const triggeredPolicies = result.policies || [];
  const nonTriggeredPolicies = result.nonTriggeredPolicies || [];
  const totalPolicies = triggeredPolicies.length + nonTriggeredPolicies.length;
  const isCompliant = triggeredPolicies.length === 0;

  return (
    <div className={styles.metadataColumn}>
      <h4 className={styles.metadataColumnTitle}>Policy Information</h4>

      {totalPolicies > 0 && (
        <>
          <div className={styles.policyColumnBadgeRow}>
            <Badge size={BadgeSize.Small} text={isCompliant ? 'ALLOWED' : 'REJECTED'} />
            <span className={styles.policyViolationCount}>
              ({triggeredPolicies.length}/{totalPolicies}) Violations
            </span>
          </div>

          {triggeredPolicies.length > 0 && (
            <>
              <span className={styles.policyColumnSubtitle}>Triggered Policies ({triggeredPolicies.length})</span>
              <div className={styles.policyColumnList}>
                {triggeredPolicies.slice(0, 6).map(policy => (
                  <Badge key={policy.id} size={BadgeSize.Small} text={policy.name} title={policy.description} />
                ))}
                {triggeredPolicies.length > 6 && <span className={styles.morePoliciesText}>+ {triggeredPolicies.length - 6} more</span>}
              </div>
            </>
          )}

          {nonTriggeredPolicies.length > 0 && (
            <>
              <span className={styles.policyColumnSubtitle}>Nontriggered Policies ({nonTriggeredPolicies.length})</span>
              <div className={styles.policyColumnList}>
                {nonTriggeredPolicies.slice(0, 4).map(policy => (
                  <Badge key={policy.id} size={BadgeSize.Small} text={policy.name} title={policy.description} />
                ))}
                {nonTriggeredPolicies.length > 4 && <span className={styles.morePoliciesText}>+ {nonTriggeredPolicies.length - 4} more</span>}
              </div>
            </>
          )}
        </>
      )}

      {totalPolicies === 0 && <MetadataRow label="Status" value="No policies configured" />}
    </div>
  );
}

// User Information Column
function UserInformationColumn() {
  // User information is typically not available in the classification testing context
  // These would be populated from the actual content record in a full content search
  return (
    <div className={styles.metadataColumn}>
      <h4 className={styles.metadataColumnTitle}>User Information</h4>
      <MetadataRow label="Type" value="Unknown" />
      <MetadataRow label="User ID" value="Unknown" />
      <MetadataRow label="Username" value="Unknown User" />
    </div>
  );
}

// Helper function to get content display information
function getContentDisplay(result: ClassificationResult) {
  const content = getContentDisplayText(result.text, result.contentUrl);

  if (result.contentType === ContentType.AUDIO && result.text) {
    return { label: 'Transcript', content };
  }

  if (result.contentType === ContentType.TEXT && result.text) {
    return { label: 'Text', content };
  }

  if (result.contentType === ContentType.USERNAME && result.text) {
    return { label: 'Username', content };
  }

  if (result.contentType === ContentType.IMAGE) {
    return { label: 'Content Text', content: result.text };
  }

  return null;
}

// Text Content Section for V2 Layout
function TextContentSectionV2({
  result,
  isContentBlurred,
  isSafe,
  onToggleBlur,
}: {
  result: ClassificationResult;
  isContentBlurred: boolean;
  isSafe: boolean;
  onToggleBlur: () => void;
}) {
  const contentDisplay = getContentDisplay(result);
  const { mediaServiceUrl } = useSystemConfig();
  const isAudio = result.contentType === ContentType.AUDIO && Boolean(result.contentUrl);

  if (!contentDisplay?.content) return null;

  const toggleLabel = isContentBlurred ? 'Show content' : 'Hide content';

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>{contentDisplay.label}</h3>
      {isAudio && (
        <div className={styles.audioPlayerSection}>
          <InlineAudioPlayer audioId={result.id || 'audio'} audioUrl={result.contentUrl as string} mediaServiceUrl={mediaServiceUrl} />
        </div>
      )}
      <div className={styles.contentBoxContainer}>
        <div className={styles.contentBox}>
          <span className={isContentBlurred ? styles.blurredText : ''}>{contentDisplay.content}</span>
        </div>
        {!isSafe && (
          <div className={styles.visibleButton}>
            <IconButton
              aria-label={toggleLabel}
              buttonSize={IconButtonSize.Small}
              icon={isContentBlurred ? Eye : StatusCircleBlock}
              iconSize={IconSize.Small}
              title={toggleLabel}
              onClick={onToggleBlur}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Image Error Display for Wide Layout
function WideImageErrorDisplay({ contentUrl }: { contentUrl: string }) {
  // prettier-ignore
  const errorMessage = 'Unable to display image preview. Ensure URL is valid. CORS restrictions may prevent preview but classification was completed.';

  return (
    <div className={styles.wideImageSection}>
      <h3 className={styles.sectionTitle}>Image Content</h3>
      <div className={styles.imageErrorContainer}>
        <p className={styles.imageErrorText}>{errorMessage}</p>
        <p className={styles.imageUrlText}>{contentUrl}</p>
      </div>
    </div>
  );
}

// Interactive Image Container for Wide Layout
function WideInteractiveImageContainer({
  canToggleBlur,
  isContentBlurred,
  onToggleBlur,
  children,
}: {
  canToggleBlur: boolean;
  isContentBlurred: boolean;
  onToggleBlur: () => void;
  children: React.ReactNode;
}) {
  const handleClick = () => {
    if (canToggleBlur) {
      onToggleBlur();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (canToggleBlur && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onToggleBlur();
    }
  };

  const title = canToggleBlur ? (isContentBlurred ? 'Click to show content' : 'Click to hide content') : undefined;

  if (canToggleBlur) {
    return (
      <div
        aria-label={isContentBlurred ? 'Show content' : 'Hide content'}
        className={`${styles.wideImageContainer} ${styles.interactiveContainer}`}
        role="button"
        tabIndex={0}
        title={title}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        {children}
      </div>
    );
  }

  return <div className={styles.wideImageContainer}>{children}</div>;
}

// Wide Image Display Section
function WideImageDisplaySection({
  result,
  isContentBlurred,
  onToggleBlur,
  isSafe,
}: {
  result: ClassificationResult;
  isContentBlurred: boolean;
  onToggleBlur: () => void;
  isSafe: boolean;
}) {
  const [imageError, setImageError] = useState(false);

  if (result.contentType !== ContentType.IMAGE || !result.contentUrl) {
    return null;
  }

  if (imageError) {
    return <WideImageErrorDisplay contentUrl={result.contentUrl} />;
  }

  const canToggleBlur = !isSafe;

  return (
    <div className={styles.wideImageSection}>
      <h3 className={styles.sectionTitle}>Image Content</h3>
      <WideInteractiveImageContainer canToggleBlur={canToggleBlur} isContentBlurred={isContentBlurred} onToggleBlur={onToggleBlur}>
        <img
          alt={`Classified content from ${formatTimestamp(result.timestamp)}`}
          className={`${styles.wideFullImage} ${isContentBlurred ? styles.blurredImage : ''}`}
          src={result.contentUrl}
          onError={() => setImageError(true)}
        />
      </WideInteractiveImageContainer>
    </div>
  );
}

/**
 * V2 Wide Layout Dialog Content
 *
 * Renders the classification result in a horizontal four-column grid layout.
 * This is used when the CLASSIFICATION_DIALOG_V2 feature flag is enabled.
 */
export function ClassificationResultDialogV2Content({ result, isContentBlurred, isSafe, onToggleBlur }: ClassificationResultDialogV2ContentProps) {
  return (
    <div className={styles.containerV2}>
      {/* Image at top for image content */}
      <WideImageDisplaySection isContentBlurred={isContentBlurred} isSafe={isSafe} result={result} onToggleBlur={onToggleBlur} />

      {/* Text/Content section for non-image content */}
      <TextContentSectionV2 isContentBlurred={isContentBlurred} isSafe={isSafe} result={result} onToggleBlur={onToggleBlur} />

      {/* Four-column metadata grid */}
      <div className={styles.metadataColumnsGrid}>
        <ContentInformationColumn result={result} />
        <ClassificationResultsColumn isSafe={isSafe} result={result} />
        <PolicyInformationColumn result={result} />
        <UserInformationColumn />
      </div>

      {/* Labels section */}
      <LabelsSectionV2 result={result} />
    </div>
  );
}
